import React, { useRef, useState, useCallback } from 'react';
import { PageLayoutContainer, NavMainContainer } from './PageLayout.styled';
import Header from './navigation/Header';
import SideNavBar from './navigation/SideNavBar';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import {
    getRefTotalHeight,
    isMobileDevice,
    updateNavBarPosition,
    isIpad,
    checkProxyActive,
    isLandscape, 
} from 'src/utils/util';
import { setBannerHeight } from '../redux/actions';
import { checkImpersonationActive } from 'src/utils/util.js';
import { useLocation } from 'react-router-dom';
import { useEffectWithErrorLogging } from 'src/utils/useEffectWithErrorLogging';
import FloatingButton from 'src/common/components/floatingButton';

/**
 * Renders the navbar component with a sign-in button if a user is not authenticated
 */

const PageLayout = (props) => {
     const isCondensedView = useSelector(
            (state) => state.loginReducer.isCondensedView
        );
    const dispatch = useDispatch();
    // Set the search params in local storage
    const { search } = useLocation();
    localStorage.setItem('urlSearchParams', search);

    const [sideNavBarOpen, setSideNavBarOpen] = useState(false);
    const [requiredBanerMargin, setRequiredBannerMargin] = useState('');
    const [dynamicHeight, setDynamicHeight] = useState('');
    const [isLandscapeMode, setIsLandscapeMode] = useState(isLandscape());
    const bannerLength = useSelector(
        (state) => state.loginReducer.bannerLength
    );

    const bannerInfo = useSelector(
        (state) => state.loginReducer.bannerInfo,
        shallowEqual
    );

    const userDetails = useSelector(
        (state) => state.loginReducer.hybrisUserDetails,
        shallowEqual
    );

    const dismissedBanner = useSelector(
        (state) => state.loginReducer.showDismissedBanner
    );

    const isImpersonationActive = useSelector(
        (state) => state.impersonationReducer.isImpersonationActive
    );
    const closePopUp = useSelector((state) => state.accountReducer.closePopUp);
    const isDestructiveEnabled = useSelector(
        (state) => state.loginReducer.isDestructiveEnabled
    );
    
    const headerRef = useRef(null);

    const isDestructive =
        isDestructiveEnabled ||
        (bannerInfo &&
            !checkImpersonationActive() &&
            !checkProxyActive() &&
            bannerInfo.filter(
                (item) => item.messageType === 'userDestructiveMessage'
            ).length > 0);

    useEffectWithErrorLogging(() => {
        const handleResize = () => {
            setIsLandscapeMode(isLandscape());
        };
        window.addEventListener('resize', handleResize);
        window.addEventListener('orientationchange', handleResize);

        return () => {
            window.removeEventListener('resize', handleResize);
            window.removeEventListener('orientationchange', handleResize);
        };
    }, []);

    const recalcBanner = useCallback(() => {
        if (!headerRef.current) return;
        const mobile = isMobileDevice();
        const landscape = isLandscapeMode;

        if (isImpersonationActive || checkProxyActive()) {
            const iBannerHeight = getRefTotalHeight(
                headerRef.current,
                mobile && !landscape ? 120 : mobile && landscape ? 0 : 0
            );
            dispatch(
                setBannerHeight(
                    checkImpersonationActive() || checkProxyActive()
                        ? iBannerHeight
                        : ''
                )
            );
            setRequiredBannerMargin(
                mobile && !landscape ? '11.2rem' : mobile && landscape ? '3.2rem' : '3.2rem'
            );
            setDynamicHeight(iBannerHeight);
        } else if (bannerLength || dismissedBanner) {
            const bannerHeight = getRefTotalHeight(
                headerRef.current,
                mobile && !landscape ? 120 : mobile && landscape ? 40 : 0
            );
            dispatch(setBannerHeight(bannerHeight));
            setRequiredBannerMargin(
                mobile && !landscape ? '11.2rem' : mobile && landscape ? '0' : '3.2rem'
            );
            setDynamicHeight(bannerHeight);
        } else {
            setRequiredBannerMargin('');
            dispatch(setBannerHeight(''));
            setDynamicHeight('');
        }
    }, [
        bannerLength,
        dismissedBanner,
        isImpersonationActive,
        isLandscapeMode,
        dispatch,
        checkProxyActive(),
    ]);


    useEffectWithErrorLogging(() => {
        // initial calc and on deps change
        recalcBanner();
    }, [recalcBanner, headerRef.current?.clientHeight]);

    useEffectWithErrorLogging(() => {
        // Stabilize after orientation/layout changes; wait for layout to settle
        let rafId;
        let timeoutId;
        const rerun = () => {
            rafId = requestAnimationFrame(() => {
                timeoutId = setTimeout(recalcBanner, 200);
            });
        };
        window.addEventListener('orientationchange', rerun);
        window.addEventListener('resize', rerun);
        return () => {
            window.removeEventListener('orientationchange', rerun);
            window.removeEventListener('resize', rerun);
            if (rafId) cancelAnimationFrame(rafId);
            if (timeoutId) clearTimeout(timeoutId);
        };
    }, [recalcBanner]);

    useEffectWithErrorLogging(() => {
        // Observe header height changes (e.g., wrapping) to keep bannerHeight in sync
        if (!headerRef.current || typeof ResizeObserver === 'undefined') return;
        const observer = new ResizeObserver(() => recalcBanner());
        observer.observe(headerRef.current);
        return () => observer.disconnect();
    }, [recalcBanner]);

    const openSideNavBarHandler = () => {
        setSideNavBarOpen(true);
        let body = document.getElementById('body');
        body.style.overflow = 'hidden';
        isIpad() && updateNavBarPosition('unset');
    };

    const closeSideNavBarHandler = () => {
        setSideNavBarOpen(false);
        let body = document.getElementById('body');
        body.style.overflow = '';
        isIpad() && updateNavBarPosition('fixed');
    };
    // const applyFixedPosition = () => {
    //     // eslint-disable-next-line no-debugger
    //     debugger;
    //     if (sessionStorage.getItem('ProxyimpersonationKey') && closePopUp) {
    //         return 'absolute';
    //     } else if (localStorage.getItem('authToken')) {
    //         return 'fixed';
    //     } else {
    //         ('unset');
    //     }
    // };
    // let proxykey = sessionStorage.getItem('ProxyimpersonationKey');
    // useEffectWithErrorLogging(() => {
    //     // eslint-disable-next-line no-debugger
    //     debugger;
    //     const position =
    //         proxykey && closePopUp
    //             ? 'absolute'
    //             : localStorage.getItem('authToken')
    //             ? 'fixed'
    //             : 'unset';
    //     setPosition(position);
    // }, [closePopUp, proxykey]);

    
    // Check if user is logged in
    const isUserLoggedIn = userDetails?.networkId;
    
    return (
        <PageLayoutContainer bannerHeight={dynamicHeight} isCondensedView={isCondensedView}>
            <Header
                bannerRef={headerRef}
                openSideNavBar={openSideNavBarHandler}
                closeSideNavBar={closeSideNavBarHandler}
            />
            {isUserLoggedIn ? <FloatingButton/> : null}
            <NavMainContainer
                className={'sideNav'}
                rquiredMargin={requiredBanerMargin}
                rquiredMarginMobile={requiredBanerMargin}
                maintainanceBackground={isDestructive}
                applyFixedPosition={
                    localStorage.getItem('authToken')
                    // sessionStorage.getItem('ProxyimpersonationKey') &&
                    // closePopUp
                    //     ? 'absolute'
                    //     : localStorage.getItem('authToken')
                    //     ? 'fixed'
                    //     : 'unset'
                }
            >
                <SideNavBar
                    showSideNavBar={sideNavBarOpen}
                    closeSideNavBar={closeSideNavBarHandler}
                />
                <main>{props.children}</main>
            </NavMainContainer>
        </PageLayoutContainer>
    );
};

export default PageLayout;